import { ProStudioContext } from './prostudio.xstate.types';

/**
 * Drops the per-page caches that are derived from the project's channels.
 *
 * Each of the Project Settings / Default Messages screens loads its data ONCE and
 * gates the load on "is my slice still null?" — a deliberate guard against
 * re-fetching on every render. Nothing invalidated those slices when a channel was
 * created or deleted, so the screens kept serving a list from before the change and
 * only a full browser reload (which throws the whole XState context away) fixed it.
 * PM hit this on Default Messages: a newly created channel was missing until F5.
 *
 * Setting the slices back to `null` restores the "not loaded yet" state, so the
 * next visit re-dispatches its own loader. It does NOT fetch anything here — the
 * user may never open those screens, and a channel mutation should not fan out
 * into an extra request per screen.
 *
 * All of these depend on the set of channels:
 *  - `defaultMessagesPypes` — one card per channel
 *  - `liveAgentPypes` — resolves the "Channels Using It" pills
 *  - `pypeConnections` — its `pypes` join rows point at channel pypes
 *  - `apiCredentials` / `channelApiCredentials` — keys are listed per channel
 *  - `pypeRuleGroups` / `pypeRuleGroupChannels` — Data Privacy gates its load on
 *    the former and renders its "Channels Using It" pills from the latter, which
 *    `loadPypeRuleGroups` builds by walking every pype in the project
 *  - `channelSettingsLoadFailures` — the per-load failure counts
 *
 * Renaming a channel counts too, not just create/delete: the name is what these
 * screens display, so a rename left them showing the old one.
 */
export const invalidateChannelDerivedCaches = (ctx: ProStudioContext): void => {
  ctx.defaultMessagesPypes = null;
  ctx.liveAgentPypes = null;
  ctx.pypeConnections = null;
  ctx.apiCredentials = null;
  ctx.channelApiCredentials = null;
  ctx.pypeRuleGroups = null;
  ctx.pypeRuleGroupChannels = null;
  // Clear the failure record with the slices it describes: a past error must
  // not stop the next visit from loading, and a stale "could not load" note is
  // as wrong as a stale list. Readers compare against the count they saw at
  // their own dispatch, so a reset that drops the counts BELOW that snapshot is
  // safe — it reads as "not failed", the chain re-dispatches, and the snapshot
  // is retaken at that dispatch.
  ctx.channelSettingsLoadFailures = null;
};
